import { type FormEvent, type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import AppShell, { EmptyState, ErrorNote } from "@/components/AppShell";
import SchemaForm, { type EventSchema } from "@/components/SchemaForm";
import { api, globalApi } from "@/lib/api";
import { formatDateTime, toLocalInputValue, useAsync, useVesselPath } from "@/lib/hooks";
import { PARAMS, formatValue, readValue } from "@/lib/params";
import type { ParamKey } from "@/lib/params";
import {
  buildTimeline,
  describeDose,
  describeEffect,
  eventDetail,
  eventLabel,
  unitLabel,
} from "@/lib/timeline";
import type {
  ChemicalOption,
  DoseList,
  Equipment,
  EventItem,
  EventList,
  LoggedDose,
  Reading,
  ReadingList,
} from "@/types";

/** The four you glance at when scanning history. The rest live on the detail
 *  page — a row crammed with ten numbers is a row nobody reads. */
const SUMMARY: ParamKey[] = ["fc", "ph", "ta", "cya"];

/** A row's own controls: quiet enough to stay out of the way while you scan,
 *  and present on every row because the entry you need to fix is never the one
 *  you expected to fix. */
const ACTION =
  "text-xs text-(--color-ink-muted) underline underline-offset-2 hover:text-(--color-ink)";

export default function History() {
  const vp = useVesselPath();
  const readings = useAsync<ReadingList>(
    () => api.get<ReadingList>("/api/readings?limit=200"),
    [],
  );

  // Doses are a separate record deliberately (see the glossary), but they are
  // the same story as the readings and belong in the same list. Fetched
  // alongside rather than merged server-side: two ordered lists interleave
  // cheaply, and a failure here must not cost you your reading history.
  const doses = useAsync<DoseList>(
    () => api.get<DoseList>("/api/doses?limit=400").catch(() => ({ items: [], total: 0 })),
    [],
  );

  // Same reasoning as doses: an events outage must not cost you the page.
  const events = useAsync<EventList>(
    () => api.get<EventList>("/api/events?limit=400").catch(() => ({ items: [], total: 0 })),
    [],
  );

  const data = readings.data;
  const timeline = buildTimeline(
    data?.items ?? [],
    doses.data?.items ?? [],
    events.data?.items ?? [],
  );

  return (
    <AppShell
      title="History"
      subtitle={
        data
          ? [
              `${data.total} reading${data.total === 1 ? "" : "s"}`,
              doses.data?.total
                ? `${doses.data.total} dose${doses.data.total === 1 ? "" : "s"}`
                : null,
              events.data?.total
                ? `${events.data.total} event${events.data.total === 1 ? "" : "s"}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      action={
        <Link to={vp("/log")} className="btn btn-quiet px-3 py-2 text-sm">
          Log
        </Link>
      }
    >
      {readings.error && <ErrorNote message={readings.error} />}
      {readings.loading && <p className="text-sm text-(--color-ink-muted)">Loading…</p>}

      {data && timeline.length === 0 && (
        <EmptyState
          title="No readings yet"
          body="Test the water and log the numbers. Everything else in the app builds on these."
          action={
            <Link to={vp("/log")} className="btn btn-primary">
              Log your first reading
            </Link>
          }
        />
      )}

      {timeline.length > 0 && (
        <ol className="space-y-2">
          {timeline.map((entry) => {
            // Each kind reloads only its own list. A corrected dose has no
            // bearing on what the readings say, and refetching all three would
            // blank the page to fix one line of it.
            if (entry.kind === "reading") {
              return (
                <li key={entry.reading.id}>
                  <ReadingRow reading={entry.reading} onChanged={readings.reload} />
                </li>
              );
            }
            if (entry.kind === "doses") {
              return (
                <li key={`doses-${entry.at}`}>
                  <DoseRow at={entry.at} doses={entry.doses} onChanged={doses.reload} />
                </li>
              );
            }
            return (
              <li key={`event-${entry.event.id}-${entry.phase}`}>
                <EventRow
                  event={entry.event}
                  phase={entry.phase}
                  at={entry.at}
                  onChanged={events.reload}
                />
              </li>
            );
          })}
        </ol>
      )}
    </AppShell>
  );
}

/**
 * One test, as it was recorded — and deliberately with no provenance on it.
 *
 * The four states answer "what is known about the water *now*", which is a
 * question about the top of this list and nothing else. Resolving them down the
 * page would mark almost every entry superseded, which is both true and useless:
 * a June reading has had two months of chlorine poured in after it, and saying
 * so is not what a logbook is for. The server takes the same line — it resolves
 * state only for the newest reading (see `resolve_for_reading`).
 *
 * Nor is anything carried here. A carried value is a read-time derivation, and
 * writing one into a row of the log would be the app showing a measurement
 * nobody took (ADR 0010, non-negotiable #7).
 *
 * What supersession looks like on this screen is already built: the dose or
 * event that moved the water is interleaved directly above the reading it moved,
 * which is the same fact told as a story rather than as a flag.
 */
function ReadingRow({ reading, onChanged }: { reading: Reading; onChanged: () => void }) {
  const vp = useVesselPath();
  const measured = SUMMARY.filter((key) => readValue(reading, key) !== null);

  return (
    <div className="card transition-colors hover:border-(--color-border-strong)">
      {/* The body stays one large tap target; the controls sit outside it,
          because a button nested in a link is a coin toss on a phone. */}
      <Link to={vp(`/history/${reading.id}`)} className="block px-4 pt-3 pb-2">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium">{formatDateTime(reading.taken_at)}</span>
          {reading.entry_mode === "quick" && <span className="unit">quick</span>}
        </div>

        {measured.length > 0 ? (
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
            {measured.map((key) => {
              const meta = PARAMS[key];
              return (
                <div key={key} className="flex items-baseline gap-1.5">
                  <dt className="unit">{meta.label}</dt>
                  <dd className="figure text-base" style={{ color: meta.color }}>
                    {formatValue(readValue(reading, key), meta)}
                  </dd>
                </div>
              );
            })}
          </dl>
        ) : (
          <p className="mt-1 truncate text-sm text-(--color-ink-muted)">
            {reading.notes ?? "No measurements recorded."}
          </p>
        )}
      </Link>

      <RowActions className="px-4 pb-3">
        <Link to={vp(`/history/${reading.id}/edit`)} className={ACTION}>
          Edit
        </Link>
        <DeleteControl
          question="Delete this reading? Charts covering this date will change."
          onDelete={() => api.delete(`/api/readings/${reading.id}`)}
          onDeleted={onChanged}
        />
      </RowActions>
    </div>
  );
}

/** One trip to the pool: everything added at the same moment.
 *
 * Visually quieter than a reading and inset, because a dose is a consequence of
 * the reading above it rather than a peer of it. Not a link — there is no dose
 * detail screen, so each line carries its own controls instead.
 */
function DoseRow({
  at,
  doses,
  onChanged,
}: {
  at: string;
  doses: LoggedDose[];
  onChanged: () => void;
}) {
  return (
    <div className="ml-4 border-l-2 border-(--color-border-subtle) py-1 pl-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="unit">{formatDateTime(at)}</span>
        {doses.some((d) => d.source === "calculator") && <span className="unit">suggested</span>}
      </div>
      <ul className="mt-0.5 space-y-2">
        {doses.map((dose) => (
          <li key={dose.id}>
            <DoseLine dose={dose} onChanged={onChanged} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One chemical, and the two things you might want to do to it.
 *
 * Per dose rather than per batch: the trip to the pool was one moment, but the
 * gallon you mistyped is one line of it, and correcting the whole application
 * to fix one number is how the other two end up wrong.
 */
function DoseLine({ dose, onChanged }: { dose: LoggedDose; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const effect = describeEffect(dose);

  if (editing) {
    return (
      <DoseEditor
        dose={dose}
        onSaved={() => {
          setEditing(false);
          onChanged();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="text-sm">
      <span className="text-(--color-ink)">Added {describeDose(dose)}</span>
      {effect && <span className="unit ml-2">{effect}</span>}
      <RowActions className="mt-0.5">
        <button type="button" className={ACTION} onClick={() => setEditing(true)}>
          Edit
        </button>
        <DeleteControl
          question="Delete this dose?"
          onDelete={() => api.delete(`/api/doses/${dose.id}`)}
          onDeleted={onChanged}
        />
      </RowActions>
    </div>
  );
}

/**
 * Correcting a dose from the row where you noticed it was wrong.
 *
 * The same fields the log form offers, in the same order, because it is the
 * same decision being re-made — and the amount is the one people get wrong,
 * standing at the pool with a jug in one hand.
 *
 * Only what actually changed is sent. That is not just tidiness: `strength` in
 * particular is a question the server answers for you when you leave it out,
 * recording the strength it assumed. Echoing the value back untouched would
 * turn its assumption into your statement and retire the note that said so.
 */
function DoseEditor({
  dose,
  onSaved,
  onCancel,
}: {
  dose: LoggedDose;
  onSaved: () => void;
  onCancel: () => void;
}) {
  // Fetched only once an editor opens. History is a screen for scanning, and
  // the registry is of no use to the ninety-nine rows nobody is correcting.
  const chemicals = useAsync<ChemicalOption[]>(
    () => globalApi.get<ChemicalOption[]>("/api/chemicals"),
    [],
  );

  const [chemical, setChemical] = useState(dose.chemical);
  const [amount, setAmount] = useState(() => plain(dose.amount));
  const [strength, setStrength] = useState(() => plain(dose.strength_percent));
  const [unit, setUnit] = useState(dose.unit);
  const [when, setWhen] = useState(() => toLocalInputValue(new Date(dose.dosed_at)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = chemicals.data ?? [];
  const option = options.find((c) => c.key === chemical);

  function chooseChemical(key: string) {
    setChemical(key);
    const next = options.find((c) => c.key === key);
    // Unit and strength follow the chemical, as they do in the log form:
    // pounds are not a choice for chlorine, and 12.5% is meaningless for
    // baking soda.
    setUnit(next?.units[0] ?? "");
    setStrength(next?.default_strength ?? "");
  }

  /** Only the fields whose value the owner actually moved. */
  function correction(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    if (chemical !== dose.chemical) patch.chemical = chemical;
    if (unit !== dose.unit) patch.unit = unit;
    if (!unchanged(amount, dose.amount)) patch.amount = amount.trim();
    if (!unchanged(strength, dose.strength_percent)) {
      patch.strength_percent = strength.trim() === "" ? null : strength.trim();
    }
    const at = new Date(when).toISOString();
    if (at !== new Date(dose.dosed_at).toISOString()) patch.dosed_at = at;
    return patch;
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const patch = correction();
    // Nothing moved, so nothing is written — an empty PUT would still bump
    // `updated_at` for a form that was only opened and looked at.
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/doses/${dose.id}`, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that correction.");
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="card p-3">
      <label className="block">
        <span className="unit">Chemical</span>
        <select
          value={chemical}
          onChange={(e) => chooseChemical(e.target.value)}
          className="field mt-1"
        >
          {/* Until the registry arrives, the dose's own chemical is the only
              honest option — an empty select would read as "none chosen". */}
          {options.length === 0 && <option value={dose.chemical}>{dose.chemical_label}</option>}
          {options.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-2 flex gap-2">
        <label className="min-w-0 flex-1">
          <span className="unit">Amount</span>
          <input
            // decimal, not numeric: half a gallon is ordinary, and `numeric`
            // hides the decimal point on iOS.
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="field figure mt-1"
          />
        </label>

        {(option === undefined || option.strengths.length > 0) && (
          <label className="w-24">
            <span className="unit">Strength</span>
            <div className="relative mt-1">
              <input
                inputMode="decimal"
                list={`strengths-${dose.id}`}
                value={strength}
                onChange={(e) => setStrength(e.target.value)}
                className="field figure pr-7"
                // Explicit, because the "%" sitting inside this label would
                // otherwise become part of the field's name.
                aria-label="Strength"
              />
              <span className="unit pointer-events-none absolute inset-y-0 right-2 flex items-center">
                %
              </span>
            </div>
            <datalist id={`strengths-${dose.id}`}>
              {(option?.strengths ?? []).map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </label>
        )}

        <label className="w-24">
          <span className="unit">Unit</span>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="field mt-1"
          >
            {option === undefined && <option value={dose.unit}>{unitLabel(dose.unit)}</option>}
            {(option?.units ?? []).map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-2 block">
        <span className="unit">When</span>
        {/* Editable because a dose logged under the wrong hour is a real
            mistake, and the timing is read by the sundown and overnight-loss
            work. Move one chemical of a batch and it becomes its own entry in
            the list, which is exactly what the correction means. */}
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="field figure mt-1"
        />
      </label>

      {chemicals.error && (
        <p className="mt-2 text-xs text-(--color-ink-faint)">
          Could not load the chemical list, so only the amount, unit and time are safe to change
          here.
        </p>
      )}

      {error && (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={saving} className="btn btn-primary flex-1 py-2 text-sm">
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-quiet flex-1 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Something that happened to the pool rather than something you measured or
 * added — rain, a backwash, the season opening.
 *
 * Inset like a dose, because it is context for the readings around it rather
 * than a peer of them, but marked differently: doses are things you chose, and
 * events are mostly things that happened to you.
 */
function EventRow({
  event,
  phase,
  at,
  onChanged,
}: {
  event: EventItem;
  phase: "start" | "end";
  at: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const detail = eventDetail(event);

  // A closed span appears twice, and both rows edit the one record — the form
  // carries both ends, so which end you started from does not matter.
  if (editing) {
    return (
      <div className="ml-4 border-l-2 border-dashed border-(--color-border-subtle) py-1 pl-3">
        <EventEditor
          event={event}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="ml-4 border-l-2 border-dashed border-(--color-border-subtle) py-1 pl-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="unit">{formatDateTime(at)}</span>
      </div>
      <p className="mt-0.5 text-sm">
        {eventLabel(event, phase)}
        {detail && <span className="unit ml-2">{detail}</span>}
      </p>
      {event.notes && (
        <p className="mt-0.5 truncate text-sm text-(--color-ink-muted)">{event.notes}</p>
      )}
      <RowActions className="mt-0.5">
        <button type="button" className={ACTION} onClick={() => setEditing(true)}>
          Edit
        </button>
        <DeleteControl
          // A span is one record shown at both ends, so deleting from either
          // end takes the whole thing. Said out loud, because the row you are
          // looking at is only half of what disappears.
          question={
            event.is_span && event.ends_at
              ? "Delete this span, start and end?"
              : "Delete this event?"
          }
          onDelete={() => api.delete(`/api/events/${event.id}`)}
          onDeleted={onChanged}
        />
      </RowActions>
    </div>
  );
}

/**
 * Correcting an event in place — the gauge you read wrong, the hour the cover
 * actually came off.
 *
 * The detail fields are rendered from the server's own description of the type
 * (`/api/events/schema/...`), which is the point of typing the JSONB: a new
 * event type stays one Pydantic class and no React work, here as in the dialog
 * that logs one.
 *
 * What it deliberately cannot change is which *kind* of event this is. The
 * payload is a discriminated union on the type, so switching it would leave a
 * cover's fields sitting on a drain. That is a different event — log it, and
 * delete this one.
 */
function EventEditor({
  event,
  onSaved,
  onCancel,
}: {
  event: EventItem;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const schema = useAsync<EventSchema>(
    () => api.get<EventSchema>(`/api/events/schema/${event.event_type}`),
    [event.event_type],
  );
  // A payload's equipment references are stored as ids, so the picker needs the
  // log to show which cover it is talking about.
  const equipment = useAsync<Equipment[]>(() => api.get<Equipment[]>("/api/equipment"), []);

  const [startsAt, setStartsAt] = useState(() => toLocalInputValue(new Date(event.starts_at)));
  const [endsAt, setEndsAt] = useState(() =>
    event.ends_at ? toLocalInputValue(new Date(event.ends_at)) : "",
  );
  const [payload, setPayload] = useState<Record<string, unknown>>(() => heldPayload(event));
  const [notes, setNotes] = useState(event.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The stored flag, not the schema's, until the schema is here: it is the same
  // answer and one of them has already arrived.
  const isSpan = schema.data?.is_span ?? event.is_span;

  /** Only the fields whose value the owner actually moved. */
  function correction(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};

    const start = new Date(startsAt).toISOString();
    if (start !== new Date(event.starts_at).toISOString()) patch.starts_at = start;

    if (isSpan) {
      const end = endsAt === "" ? null : new Date(endsAt).toISOString();
      const stored = event.ends_at ? new Date(event.ends_at).toISOString() : null;
      // Emptied means "still going", and null is how you say so — the server
      // reads it as reopening the span rather than as an omitted field.
      if (end !== stored) patch.ends_at = end;
    }

    const answers = answered(payload);
    if (!sameAnswers(answers, heldPayload(event))) {
      // Sent whole, discriminator included: the server replaces the payload
      // rather than merging into it, so a field left out is a field erased.
      patch.payload = { event_type: event.event_type, ...answers };
    }

    const note = notes.trim() === "" ? null : notes;
    if (note !== event.notes) patch.notes = note;

    return patch;
  }

  async function save(submitted: FormEvent) {
    submitted.preventDefault();
    const patch = correction();
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/events/${event.id}`, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that correction.");
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="card p-3">
      <p className="unit capitalize">
        {schema.data?.label ?? event.event_type.replace(/_/g, " ")}
      </p>

      <label className="mt-2 block">
        <span className="unit">{isSpan ? "Started" : "When"}</span>
        <input
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          className="field figure mt-1"
        />
      </label>

      {isSpan && (
        <div className="mt-2">
          <label className="block">
            <span className="unit">Ended</span>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="field figure mt-1"
            />
          </label>
          {/* Outside the label: this is a description of the field, not part of
              what the field is called. */}
          <p className="mt-1 text-xs text-(--color-ink-faint)">
            Leave blank if it is still going.
          </p>
        </div>
      )}

      {schema.data && schema.data.fields.length > 0 && (
        <div className="mt-3">
          <SchemaForm
            fields={schema.data.fields}
            values={payload}
            equipment={equipment.data ?? []}
            onChange={(name, value) => setPayload((prev) => ({ ...prev, [name]: value }))}
          />
        </div>
      )}

      <label className="mt-3 block">
        <span className="unit">Notes</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="field mt-1 resize-y"
        />
      </label>

      {schema.error && (
        <p className="mt-2 text-xs text-(--color-ink-faint)">
          Could not load this event's detail fields, so only the times and the note are safe to
          change here.
        </p>
      )}

      {error && (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={saving} className="btn btn-primary flex-1 py-2 text-sm">
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-quiet flex-1 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The strip a row's controls sit in — below the content rather than beside it,
 *  because at 390px the date line is already carrying a badge. */
function RowActions({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className ?? ""}`}>
      {children}
    </div>
  );
}

/**
 * A destructive control that asks first, in the row it would empty.
 *
 * Inline rather than a dialog: the thing you are about to delete should stay on
 * screen while you decide, and a modal on a phone covers exactly the row you
 * were trying to read. Nothing here is undoable, and a logbook that loses an
 * entry to a mis-tap is worse than one that asks twice.
 */
function DeleteControl({
  question,
  onDelete,
  onDeleted,
}: {
  question: string;
  onDelete: () => Promise<unknown>;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await onDelete();
      onDeleted();
    } catch (err) {
      // Left confirming, so the answer to a failed delete is one tap rather
      // than finding the row again.
      setError(err instanceof Error ? err.message : "Could not delete that.");
      setBusy(false);
    }
  }

  if (!confirming) {
    return (
      <button type="button" className={ACTION} onClick={() => setConfirming(true)}>
        Delete
      </button>
    );
  }

  return (
    <>
      <span className="text-sm">{question}</span>
      <button
        type="button"
        onClick={remove}
        disabled={busy}
        className="text-xs font-semibold text-(--color-status-high) underline underline-offset-2"
      >
        {busy ? "Deleting…" : "Delete"}
      </button>
      <button type="button" className={ACTION} onClick={() => setConfirming(false)}>
        Keep it
      </button>
      {error && <span className="text-xs text-(--color-status-high)">{error}</span>}
    </>
  );
}

/** An event's payload as the form holds it: everything but `event_type`, which
 *  is the discriminator rather than a field anyone edits. */
function heldPayload(event: EventItem): Record<string, unknown> {
  const held: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event.payload ?? {})) {
    if (key !== "event_type") held[key] = value;
  }
  return held;
}

/** The payload fields that were actually answered. A blank is not a zero and
 *  not a false — it is a question left alone, and it is left out. */
function answered(values: Record<string, unknown>): Record<string, unknown> {
  const given: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== "" && value !== undefined) given[key] = value;
  }
  return given;
}

/** Whether two payloads carry the same answers.
 *
 * Shallow and compared as text: these are flat JSONB objects whose numbers
 * arrive as strings, and a field nobody touched still holds exactly what the
 * server sent for it.
 */
function sameAnswers(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => String(a[key] ?? "") === String(b[key] ?? ""));
}

/** "1.00" as "1", "12.50" as "12.5" — the figure the way it was typed. */
function plain(value: string | null): string {
  if (value === null) return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : value;
}

/** Whether a field still holds the figure it was seeded with.
 *
 * Compared as numbers rather than text: the record carries "12.50" where the
 * field shows "12.5", and treating that as an edit would tell the server the
 * owner stated a strength they had only looked at.
 */
function unchanged(field: string, stored: string | null): boolean {
  const typed = field.trim();
  if (typed === "") return stored === null;
  return stored !== null && Number(typed) === Number(stored);
}
